import { getEntityState } from '../entity';
import { CANVAS_TEXT_CAP_MARGIN_RATIO, SHAPE_STYLES } from '../constants';
import { COLORS } from '../constants-colors';
import {
  addV2,
  scaleV2,
  v2,
  multiplyColorAlpha255,
  isColorVisible,
  subtractV2,
} from '../helpers';
import {
  VisibilityState,
  GameManager,
  RenderItemType,
  Shape,
  TextAlignHor,
  TextBlock,
  TextLine,
  TextObject,
  V2,
  Word,
  WordStyle,
  WordType,
  type RenderShadow,
  type TextEnt,
  type Color,
  type TextObjectStyle,
} from '../types';
import * as DataController from '../controllers/data_controller';

import {
  EMPTY_RENDER_TRANSFORM,
  type RenderPackageCircle,
  type RenderPackageImage,
  type RenderPackageItem,
  type RenderPackagePoint,
  type RenderPackageRect,
  type RenderPackageText,
} from '../render_package';
import logger from '../controllers/logger';

export function createPackage(tObj: TextObject, position: V2, scale: number): RenderPackageItem[] {
  const rPackage: RenderPackageItem[] = [];
  const state = getEntityState(tObj);
  const textStyle = tObj.textStyle;
  const m = tObj.manager;

  let height = tObj.height;
  let width = tObj.width;

  //Background shape
  let color = multiplyColorAlpha255(
    tObj.highlighted ? textStyle.colorHighlight : textStyle.color,
    tObj.backgroundAlpha,
  );
  let shadow = tObj.dragged ? tObj.renderShadowDrag : tObj.renderShadow;

  let drawBackground = tObj.shapeStyle || isColorVisible(color) || textStyle.strokeWeight;

  if (drawBackground) {
    if (tObj.shape === Shape.RECT) {
      {
        // let backgroundPos = pos;
        let backGroundWidth = width;

        let rect: RenderPackageRect = {
          type: RenderItemType.rect,
          ...EMPTY_RENDER_TRANSFORM,
          positionX: position.x,
          positionY: position.y,
          scale,
          width: backGroundWidth,
          height: height,

          cornerRadius1: textStyle.cornerRadius[0],
          cornerRadius2: textStyle.cornerRadius[1],
          cornerRadius3: textStyle.cornerRadius[2],
          cornerRadius4: textStyle.cornerRadius[3],
          shadow: shadow,
          shapeStyle: null,
        };

        if (tObj.shapeStyle) {
          rect.shapeStyle = tObj.shapeStyle;
        } else {
          rect.shapeStyle = { ...SHAPE_STYLES.NONE, fillColor: color };
        }
        rPackage.push(rect);
      }
    } else if (tObj.shape === Shape.CIRCLE) {
      let circle: RenderPackageCircle = {
        type: RenderItemType.circle,
        ...EMPTY_RENDER_TRANSFORM,
        scale,
        positionX: position.x,
        positionY: position.y,
        radius: tObj.radius,
        shapeStyle: { ...SHAPE_STYLES.NONE, fillColor: color },
        shadow: shadow,
      };
      rPackage.push(circle);
    }
  }

  if (tObj.question) {
    let radius = 45;
    let iconPos = addV2(position, scaleV2(v2(-tObj.textEnt.width / 2 - radius / 2 - 10, 0), scale));

    let im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: iconPos.x - (radius / 2) * scale,
      positionY: iconPos.y - (radius / 2) * scale,
      imageId: 'iconQuestion',
      width: radius,
      height: radius,
      rotation: 0,
      opacity: 1,
      radius: 0,
      shape: Shape.RECT,
      flipHorizontally: false,
    };
    rPackage.push(im);
  }

  //only render text in default state
  if (!tObj.onlyShowInDefaultState || state === VisibilityState.DEFAULT) {
    if (!tObj.hideAllText) {
      const subPosition = scaleV2(addV2(tObj.textEnt.pos, tObj.textEnt.posOffset), scale);

      createTextEntPackage(
        tObj.textEnt,
        addV2(position, subPosition),
        scale,
        tObj.opacity,
        tObj.manager,
        tObj.textShadow,
        tObj.textStyle,
        rPackage,
      );
    }
  }

  return rPackage;
}

function createTextEntPackage(
  textEnt: TextEnt,
  pos: V2,
  scale: number,
  opacity: number,
  manager: GameManager,
  shadow: RenderShadow,
  defaultStyle: TextObjectStyle,
  rPackage: RenderPackageItem[],
): void {
  for (let i = 0; i < textEnt.textBlocks.length; i++) {
    const blockPos = addV2(pos, scaleV2(textEnt.textBlocks[i].pos, scale));
    renderTextBlockPackage(
      textEnt.textBlocks[i],
      blockPos,
      scale,
      opacity,
      manager,
      shadow,
      defaultStyle,
      rPackage,
    );
  }
}

function renderTextBlockPackage(
  textBlock: TextBlock,
  pos: V2,
  scale: number,
  opacity: number,
  manager: GameManager,
  shadow: RenderShadow,
  defaultStyle: TextObjectStyle,
  rPackage: RenderPackageItem[],
): void {
  for (let i = 0; i < textBlock.lines.length; i++) {
    const linePos = addV2(pos, scaleV2(textBlock.lines[i].localPos, scale));
    renderTextLinePackage(
      textBlock.lines[i],
      linePos,
      scale,
      opacity,
      manager,
      shadow,
      defaultStyle,
      rPackage,
    );
  }

  if (false) {
    let rect: RenderPackageRect = {
      type: RenderItemType.rect,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: pos.x + (scale * textBlock.width) / 2,
      positionY: pos.y + (scale * textBlock.height) / 2,
      width: textBlock.width,
      height: textBlock.height,
      opacity: 1,

      shapeStyle: SHAPE_STYLES.DEBUG,
      cornerRadius1: 0,
      cornerRadius2: 0,
      cornerRadius3: 0,
      cornerRadius4: 0,
      shadow: null,
    };

    rPackage.push(rect);
  }

}

function renderTextLinePackage(
  textLine: TextLine,
  pos: V2,
  scale: number,
  opacity: number,
  manager: GameManager,
  shadow: RenderShadow,
  defaultStyle: TextObjectStyle,
  rPackage: RenderPackageItem[],
): void {
  for (let i = 0; i < textLine.words.length; i++) {
    const word = textLine.words[i];
    const wordPos = addV2(pos, scaleV2(word.localPos, scale));
    if (word.type === WordType.ListItemStart) {
      if (word.visibleText) {
        createTextIconPackage(word, wordPos, scale, manager, rPackage);
      }
    } else if (word.type === WordType.Anchor) {
      if (word.visibleText) {
        createTextAnchorPackage(word, wordPos, scale, opacity, rPackage);
      }
    } else if (word.type !== WordType.Space) {
      let style = word.wordStyle;
      if (false && word.hover) {
        style = { ...word.wordStyle, defaultTextColor: COLORS.BLACK_50 };
      }
      createTextPackage(
        textLine.words[i],
        wordPos,
        scale,
        opacity,
        style,
        shadow,
        defaultStyle,
        rPackage,
      );
    }
  }
}

function createTextPackage(
  word: Word,
  pos: V2,
  scale: number,
  opacity: number,
  wordStyle: WordStyle,
  shadow: RenderShadow,
  defaultStyle: TextObjectStyle,
  rPackage: RenderPackageItem[],
): void {
  const text = word.visibleText;

  if (text.length === 0) return;

  const textColor = word.textColor || wordStyle.defaultTextColor || defaultStyle.defaultTextColor;

  const color = multiplyColorAlpha255(textColor, word.colorAlpha);

  let rText: RenderPackageText = {
    type: RenderItemType.text,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: pos.x,
    positionY: pos.y + wordStyle.fontSizePx * CANVAS_TEXT_CAP_MARGIN_RATIO,
    hAlign: TextAlignHor.LEFT,
    fillColor: color,
    strokeWeight: 0,
    strokeColor: [0, 0, 0],
    size: wordStyle.fontSizePx,
    text: text,
    fontId: wordStyle.fontId,
    shadow: shadow,
    opacity,
  };
  rPackage.push(rText);

  if (false) {
    let p: RenderPackagePoint = {
      type: RenderItemType.point,
      ...EMPTY_RENDER_TRANSFORM,
      positionX: pos.x,
      positionY: pos.y,
      size: 3,
      color: COLORS.RED_SALSA,
    };
    rPackage.push(p);
  }
}

function createTextIconPackage(
  word: Word,
  pos: V2,
  scale: number,
  manager: GameManager,
  rPackage: RenderPackageItem[],
): void {
  const size = 0.8 * word.wordStyle.fontSizePx;
  const offset = -0.85 * word.wordStyle.fontSizePx;
  const asteriskId = 'star';

  const aspect = DataController.getImageAspectRatio(asteriskId);

  const alpha = word.colorAlpha / 255;

  if (aspect) {
    const _pos = addV2(pos, scaleV2(v2(offset, word.height / 2), scale));
    const localPos = subtractV2(_pos, scaleV2(v2(size / 2, size / 2), scale));
    let im: RenderPackageImage = {
      type: RenderItemType.image,
      ...EMPTY_RENDER_TRANSFORM,
      scale,
      positionX: localPos.x,
      positionY: localPos.y,
      rotation: 0,
      imageId: asteriskId,
      width: size,
      height: size,
      opacity: alpha,
      radius: 0,
      shape: Shape.RECT,
      flipHorizontally: false,
    };
    rPackage.push(im);
  }
}

function createTextAnchorPackage(
  word: Word,
  pos: V2,
  scale: number,
  opacity: number,
  rPackage: RenderPackageItem[],
): void {
  const size = 0.8 * word.wordStyle.fontSizePx;
  const offsetX = size / 2;
  const offsetY = size / 8;

  const wordOpacity = word.colorAlpha / 255;

  const _pos = addV2(pos, scaleV2(v2(offsetX, word.height / 2 - offsetY), scale));
  let circle: RenderPackageCircle = {
    type: RenderItemType.circle,
    ...EMPTY_RENDER_TRANSFORM,
    scale,
    positionX: _pos.x,
    positionY: _pos.y,
    radius: size / 2,
    rotation: 0,
    opacity: opacity * wordOpacity,
    shapeStyle: SHAPE_STYLES.BLUE_NEW,
    shadow: null,
  };
  rPackage.push(circle);
}
